import React from "react";
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Typography,
  Icon,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  alpha,
  useTheme,
  Badge,
  Tooltip,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BusinessUser } from "../../types";

const TRANSITION_DURATION = 0.2;
const HOVER_ANIMATION = {
  transition: `all ${TRANSITION_DURATION}s cubic-bezier(0.4, 0, 0.2, 1)`,
};

interface HeaderProps {
  drawerWidth: number;
  onDrawerToggle: () => void;
  onLogout: () => void;
  user: any;
  mobileOpen: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  drawerWidth,
  onDrawerToggle,
  onLogout,
  user,
  mobileOpen,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [userMenuAnchor, setUserMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  const [notificationsAnchor, setNotificationsAnchor] =
    React.useState<null | HTMLElement>(null);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchor(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const getBusinessName = () => {
    if (!user) return "Business Dashboard";
    return (user as BusinessUser)?.business_name || "Business Dashboard";
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        maxHeight: "72px",
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        background: "transparent",
        backdropFilter: "blur(10px)",
        borderBottom: `1px solid ${alpha(
          theme.palette.divider,
          theme.palette.mode === "dark" ? 0.1 : 0.05
        )}`,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(to right, ${alpha(
                  theme.palette.background.default,
                  0.95
                )}, ${alpha(theme.palette.background.default, 0.98)})`
              : `linear-gradient(to right, ${alpha(
                  theme.palette.background.paper,
                  0.9
                )}, ${alpha(theme.palette.background.paper, 0.98)})`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
          zIndex: -1,
        },
      }}
    >
      <Toolbar
        sx={{
          height: { xs: 70, md: 80 },
          px: { xs: 2, md: 3, lg: 4 },
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Left Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <IconButton
              onClick={onDrawerToggle}
              sx={{
                display: { sm: "none" },
                ...HOVER_ANIMATION,
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
                "& .menu-toggle": {
                  transition: "transform 0.3s ease",
                  transform: mobileOpen ? "rotate(180deg)" : "rotate(0deg)",
                },
              }}
            >
              <Icon className="menu-toggle">menu_open</Icon>
            </IconButton>

            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  letterSpacing: "-0.3px",
                }}
              >
                {getBusinessName()}
              </Typography>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ height: 24, my: "auto" }}
              />
            </Box>
          </Box>

          {/* Right Section */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Quick Actions */}
            {/* <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
              <Tooltip title="Search">
                <IconButton
                  sx={{
                    ...HOVER_ANIMATION,
                    color: "text.secondary",
                    "&:hover": {
                      color: "primary.main",
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  <Icon>search</Icon>
                </IconButton>
              </Tooltip>
            </Box> */}

            {/* <Divider
              orientation="vertical"
              flexItem
              sx={{ height: 24, mx: 1 }}
            /> */}

            {/* Notifications */}
            {/* <Tooltip title="Notifications">
              <IconButton
                onClick={handleNotificationsOpen}
                sx={{
                  position: "relative",
                  ...HOVER_ANIMATION,
                  color: notificationsAnchor
                    ? "primary.main"
                    : "text.secondary",
                  "&:hover": {
                    color: "primary.main",
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Badge
                  badgeContent={3}
                  color="error"
                  sx={{
                    "& .MuiBadge-badge": {
                      ...HOVER_ANIMATION,
                      right: 5,
                      top: 5,
                    },
                  }}
                >
                  <Icon>notifications</Icon>
                </Badge>
              </IconButton>
            </Tooltip> */}

            {/* User Profile */}
            <Box
              onClick={handleUserMenuOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1,
                px: 1.5,
                ml: 1,
                borderRadius: 2,
                cursor: "pointer",
                bgcolor: userMenuAnchor
                  ? alpha(theme.palette.primary.main, 0.12)
                  : alpha(theme.palette.primary.main, 0.04),
                ...HOVER_ANIMATION,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  transform: "translateY(-1px)",
                  boxShadow: `0 4px 8px ${alpha(
                    theme.palette.common.black,
                    0.04
                  )}`,
                },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: theme.palette.primary.main,
                  fontSize: "1rem",
                  fontWeight: 600,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  ...HOVER_ANIMATION,
                }}
              >
                {user?.first_name?.[0]}
                {user?.last_name?.[0]}
              </Avatar>
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "block",
                  }}
                >
                  {getBusinessName()}
                </Typography>
              </Box>
              <Icon
                sx={{
                  fontSize: 20,
                  color: "text.secondary",
                  transform: userMenuAnchor ? "rotate(180deg)" : "none",
                  opacity: userMenuAnchor ? 1 : 0.7,
                  ...HOVER_ANIMATION,
                }}
              >
                expand_more
              </Icon>
            </Box>
          </Box>
        </Box>
      </Toolbar>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleNotificationsClose}
        onClick={handleNotificationsClose}
        PaperProps={{
          elevation: 0,
          sx: {
            width: 320,
            maxHeight: "calc(100vh - 100px)",
            overflow: "auto",
            mt: 1.5,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[8],
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You have 3 unread notifications
          </Typography>
        </Box>
        <Divider />
        <MenuItem sx={{ py: 2 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Avatar sx={{ bgcolor: "primary.main" }}>
              <Icon>shopping_cart</Icon>
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                New Order Received
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Order #1234 needs processing
              </Typography>
              <Typography variant="caption" color="text.disabled">
                2 minutes ago
              </Typography>
            </Box>
          </Box>
        </MenuItem>
        <Divider />
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Button
            fullWidth
            size="small"
            sx={{ borderRadius: 2 }}
            startIcon={<Icon>notifications</Icon>}
          >
            View All Notifications
          </Button>
        </Box>
      </Menu>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        onClick={handleUserMenuClose}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            minWidth: 320,
            overflow: "visible",
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 20,
              width: 12,
              height: 12,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
              borderLeft: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {/* User Info Section */}
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.08
            )}, ${alpha(theme.palette.background.paper, 0)})`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: theme.palette.primary.main,
                fontSize: "1.25rem",
                fontWeight: 600,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              {user?.first_name?.[0]}
              {user?.last_name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "success.main",
                    boxShadow: `0 0 0 2px ${alpha(
                      theme.palette.success.main,
                      0.2
                    )}`,
                  }}
                />
                Active Now
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Icon sx={{ fontSize: 16 }}>store</Icon>
            {getBusinessName()}
          </Typography>
        </Box>

        <Box sx={{ py: 1.5 }}>
          {/* Quick Actions */}
          {[
            {
              icon: "account_circle",
              label: "My Profile",
              description: "View and edit your profile",
              onClick: () => navigate("/business/settings"),
            },
            {
              icon: "settings",
              label: "Settings",
              description: "Manage your preferences",
              onClick: () => navigate("/business/settings"),
            },
            // {
            //   icon: "help_outline",
            //   label: "Help Center",
            //   description: "Get help and support",
            //   onClick: () => {},
            // },
          ].map((item) => (
            <MenuItem
              key={item.label}
              onClick={item.onClick}
              sx={{
                py: 1.5,
                px: 3,
                ...HOVER_ANIMATION,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  "& .menu-icon": {
                    color: "primary.main",
                    transform: "scale(1.1)",
                  },
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Icon
                  className="menu-icon"
                  sx={{
                    fontSize: 20,
                    color: "text.secondary",
                    ...HOVER_ANIMATION,
                  }}
                >
                  {item.icon}
                </Icon>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Box>

        <Divider sx={{ my: 0.5 }} />

        {/* Sign Out Button */}
        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={onLogout}
            startIcon={<Icon>logout</Icon>}
            sx={{
              borderRadius: 1.5,
              py: 1,
              ...HOVER_ANIMATION,
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: `0 4px 8px ${alpha(theme.palette.error.main, 0.15)}`,
              },
            }}
          >
            Sign Out
          </Button>
        </Box>
      </Menu>
    </AppBar>
  );
};
